import { USER_ID_KEY, USER_TOKEN_KEY } from "../../../core/constants/app_constants";
import { toLoginResultModel } from "../../domain/mapper/login_result_mapper";
import BasicResultModel from "../../domain/model/basic_result_model";
import UserRepository from "../../domain/repository/user_repository";
import { toUserModel } from "../../domain/mapper/user_result_mapper";
import { toPaymentMethodModel } from "../../domain/mapper/payment_method_result_mapper";
import { toBillingAddressModel } from "../../domain/mapper/billing_address_result_mapper";
import { trackingService} from '../../../features/presentation/di/di';

class UserRepositoryImpl extends UserRepository {
  static #instance = null; // Private static instance

  constructor(userAPIService, localStorageService) {
    if (UserRepositoryImpl.#instance) {
      return UserRepositoryImpl.#instance; // Return the same instance if already created
    }
    super(userAPIService, localStorageService);
    UserRepositoryImpl.#instance = this; // Store the instance
  }

  async login(email, password, rememberMe) {
    const data = { email, password, rememberMe };
    const result = await this.userAPIService.login(data);
    const resultModel = toLoginResultModel(result);
    await this.localStorageService.saveData(USER_TOKEN_KEY, resultModel.token); //Save User Token to local
    await this.localStorageService.saveData(USER_ID_KEY, resultModel.id); //Save User ID to local
    return resultModel;
  }

  async logout() {
    await this.localStorageService.deleteData(USER_TOKEN_KEY);
    await this.localStorageService.deleteData(USER_ID_KEY);
  }

  async register(name, email, password) {
    const data = { name, email, password };
  const result = await this.userAPIService.register(data);
    console.log(result);
    return new BasicResultModel(result.success, result.message);
  }

  async getUser() {
    const userId = await this.localStorageService.getData(USER_ID_KEY);
    const userToken = await this.localStorageService.getData(USER_TOKEN_KEY);
    if (!userToken) {
      await this.logout();
      console.log("Get User", null);
      return null;
    }
    const result = await this.userAPIService.getUser(userToken, { userId });
    const resultModel = toUserModel(result)?.copyWith({ token: userToken });
    trackingService.setUserProperties(userId, {
      name: resultModel.name,
      email: resultModel.email,
      tier: resultModel.tier,
      isSubscribed: resultModel.tierId !== '',
      planExpirationDate: resultModel.planExpirationDate,
    });
    return resultModel;
  }

  /**
   * Validate OTP
   * @param {string} email - The Email of the user
   * @param {string} otp - The OTP of the user
   * @returns {BasicResultModel} - The result of the validation
   */
  async validateOtp(email, otp) {
    const data = { email, otp };
    const result = await this.userAPIService.validateOtp(data);

    return new BasicResultModel(result.success, result.message);
  }

  /**
   * Resends the OTP to the user
   * @param {string} email - The email address of the user
   * @returns {BasicResultModel} - The result of the resend OTP operation
   */
  async resendOtp(email) {
    const result = await this.userAPIService.resendOtp(email);
    return new BasicResultModel(result.success, result.message);
  }

  async sendChangePasswordOtp(userId) {
    const result = await this.userAPIService.sendChangePasswordOtp(userId);
    return new BasicResultModel(result.success, result.message);
  }

  async validateChangePassOTP(userId, otp) {
    const result = await this.userAPIService.validateChangePassOTP(userId, otp);
    return result;
  }

  async changePassword(email, oldPassword, newPassword) {
    const result = await this.userAPIService.changePassword(email, oldPassword, newPassword);
    return result;
  }

  async sendRecoveryLink(email) {
    const result = await this.userAPIService.sendRecoveryLink(email);
    return result;
  }

  async validateRecoveryLink(token) {
    const result = await this.userAPIService.validateRecoveryLink(token);
    return result;
  }

  async resetPassword(token, password) {
    const result = await this.userAPIService.resetPassword(token, password);
    return result;
  }

  async sendOTP(email) {
    const result = await this.userAPIService.sendOTP(email);
    return new BasicResultModel(result.success, result.message);
  }

  async validateOTP(email, otp) {
    const result = await this.userAPIService.validateOTP({ email, otp });
    return new BasicResultModel(result.success, result.message);
  }

  async updateProfile({ name, email }) {
    const userToken = await this.localStorageService.getData(USER_TOKEN_KEY);
    if (!userToken) {
      throw new Error("User not authenticated");
    }
    const result = await this.userAPIService.updateProfile({ name, email });
    return new BasicResultModel(result.success, result.message);
  }

  async userChangePassword(data) {
    // Validate that new password and confirm password match
    if (data.newPassword !== data.confirmPassword) {
      return new BasicResultModel(false, "New password and confirm password do not match");
    }

    // Validate password strength
    if (!data.newPassword || data.newPassword.length < 8) {
      return new BasicResultModel(false, "Password must be at least 8 characters long");
    }

    const result = await this.userAPIService.userChangePassword(data);
    return new BasicResultModel(result.success, result.message);
  }

  async userSendContactUs({ subject, message }) {
    const result = await this.userAPIService.userSendContactUs({ subject, message });
    return new BasicResultModel(result.success, result.message);
  }

  async userSendFeedBack({ message }) {
    const result = await this.userAPIService.userSendFeedBack({ message });
    return new BasicResultModel(result.success, result.message);
  }

  async userSendPublicContactUs({ name, email, message }) {
    const result = await this.userAPIService.userSendPublicContactUs({ name: name, email: email, message: message });
    console.log("Public Contact Us", result);
    return new BasicResultModel(result.success, result.message);
  }

  async getPaymentMethods() {
    const result = await this.userAPIService.getPaymentMethods();
    console.log("Payment Methods Response", result);
    if (!result.success) {
      return new BasicResultModel(false, result.message);
    }
    const paymentMethods = await Promise.all(
      result.data.map(async (paymentMethod) => {
        return await toPaymentMethodModel(paymentMethod, this.encryptionService);
      })
    );
    console.log("Payment Methods", paymentMethods);
    return new BasicResultModel(result.success, result.message, paymentMethods);
  }

  async getBillingAddresses() {
    const result = await this.userAPIService.getBillingAddresses();
    if (!result.success) {
      return new BasicResultModel(false, result.message);
    }
    const billingAddresses = await Promise.all(
      result.data.map(async (billingAddress) => {
        return await toBillingAddressModel(billingAddress, this.encryptionService);
      })
    );
    console.log("Billing Addresses", billingAddresses);
    return new BasicResultModel(result.success, result.message, billingAddresses);
  }
}

export default UserRepositoryImpl;
